import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faInfoCircle } from '@rq-ratings/pro-regular-svg-icons'
import { useMutation } from '@tanstack/react-query'
import React from 'react'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import invariant from 'tiny-invariant'

import { SubscriptionType } from '../../enums/SubscriptionType'
import useCurrentCompanyOrFail from '../../hooks/useCurrentCompanyOrFail'
import useCurrentCompanyUserOrFail from '../../hooks/useCurrentCompanyUserOrFail'
import useInvalidateSubscriptionsQuery from '../../hooks/useInvalidateSubscriptionsQuery'
import useNotyf from '../../hooks/useNotyf'
import useSubscriptionsQuery from '../../hooks/useSubscriptionsQuery'
import { LONG_MESSAGE_DURATION } from '../../lib/constants'
import subscriptionService from '../../lib/services/subscriptionService'
import OpenChatButton from '../misc/OpenChatButton'
import Banner from './Banner'

const SubscriptionExpiringBanner: React.FC = () => {
  const subscriptionsQuery = useSubscriptionsQuery()
  const subscriptions = subscriptionsQuery.data
  const invalidateSubscriptionsQuery = useInvalidateSubscriptionsQuery()
  const currentCompanyUser = useCurrentCompanyUserOrFail()
  const currentCompany = useCurrentCompanyOrFail()
  const notyf = useNotyf()

  const dismissMutation = useMutation({
    mutationFn: ({
      subscriptionId,
      expiringUrgently,
    }: {
      subscriptionId: number
      expiringUrgently: boolean
    }) =>
      expiringUrgently
        ? subscriptionService.dismissExpiringUrgentlyBanner(subscriptionId)
        : subscriptionService.dismissExpiringSoonBanner(subscriptionId),
    onSuccess: () => {
      notyf.open({
        type: 'success',
        message:
          'You can still get in touch to renew your subscription by clicking the chat button in the bottom-right corner.',
        duration: LONG_MESSAGE_DURATION,
      })

      invalidateSubscriptionsQuery(currentCompany.id, currentCompanyUser.id)
    },
    onError: () => {
      notyf.error('There was an error dismissing the banner.')
    },
  })

  const expiringSubscriptions = subscriptions
    .filter(
      (subscription) =>
        (subscription.expiringSoon &&
          !subscription.expiringSoonBannerDismissed) ||
        (subscription.expiringUrgently &&
          !subscription.expiringUrgentlyBannerDismissed),
    )
    .toSorted((a, b) => {
      if (a.expiredAt === null) {
        return 1
      }

      if (b.expiredAt === null) {
        return -1
      }

      return a.expiredAt.valueOf() - b.expiredAt.valueOf()
    })

  const subscription = expiringSubscriptions.find((_x) => true) // Find the first expiring subscription

  invariant(subscription, 'No expiring subscription found')

  function getSubscriptionTypeDescription(subscriptionType: SubscriptionType) {
    switch (subscriptionType) {
      case SubscriptionType.CalendarIntegration:
        return 'calendar Integration'
      case SubscriptionType.PersonalEmailIntegration:
        return 'personal email integration'
      case SubscriptionType.SharedEmailIntegration:
        return 'shared email integration'
      default:
        throw 'Unsupported subscription type'
    }
  }

  function getTooltip(subscriptionType: SubscriptionType) {
    switch (subscriptionType) {
      case SubscriptionType.CalendarIntegration:
      case SubscriptionType.PersonalEmailIntegration:
        return 'Continued subscription includes both personal email integration and calendar integration, and is priced at £5 per calendar month. Contact us to arrange.'

      // Picking this out specifically, as we explicitly don't want a tooltip here
      // (I don't want anyone to think this case is unhandled)
      case SubscriptionType.SharedEmailIntegration:
      default:
        return null
    }
  }

  const tooltip = getTooltip(subscription.subscriptionType)

  return (
    <Banner
      isHiding={dismissMutation.isPending}
      onHide={() =>
        dismissMutation.mutate({
          subscriptionId: subscription.id,
          expiringUrgently: subscription.expiringUrgently,
        })
      }
      variant={subscription.expiringUrgently ? 'danger' : 'primary'}
    >
      Your {getSubscriptionTypeDescription(subscription.subscriptionType)}{' '}
      subscription is expiring soon. Please{' '}
      <OpenChatButton
        usePlainButtonClasses={false}
        className="btn btn-light mx-1"
      >
        get in touch
      </OpenChatButton>{' '}
      to renew.
      {tooltip !== null && (
        <OverlayTrigger
          overlay={<Tooltip>{tooltip}</Tooltip>}
          placement="bottom"
        >
          <FontAwesomeIcon icon={faInfoCircle} className="mx-2" />
        </OverlayTrigger>
      )}
    </Banner>
  )
}

export default SubscriptionExpiringBanner
